import { useMutation } from '@tanstack/react-query';
import { CopyIcon, DicesIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { User } from '@/common/user';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { sendPasswordResetEmailOptions } from '@/queries/users/send-password-reset-email';
import { setTempPasswordOptions } from '@/queries/users/set-temp-password';

interface PasswordManagementDialogProps {
	isOpen: boolean;
	onClose: () => void;
	user: User | null;
}

export function PasswordManagementDialog({ isOpen, onClose, user }: PasswordManagementDialogProps) {
	const { t } = useTranslation();
	const [tempPassword, setTempPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);

	const setTempPasswordApi = useMutation(setTempPasswordOptions());
	const sendPasswordResetEmailApi = useMutation(sendPasswordResetEmailOptions());

	const handleClose = () => {
		setTempPassword('');
		setShowPassword(false);
		onClose();
	};

	const handleSetTemporaryPassword = async () => {
		if (tempPassword.trim().length >= 8) {
			await setTempPasswordApi.mutateAsync({ id: user?.id as string, payload: { password: tempPassword } });
			handleClose();
		}
	};

	const handleSendResetEmail = async () => {
		await sendPasswordResetEmailApi.mutateAsync({ id: user?.id as string });
		handleClose();
	};

	const generateRandomPassword = () => {
		const length = 12;
		const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
		let password = '';
		for (let i = 0; i < length; i++) {
			password += charset.charAt(Math.floor(Math.random() * charset.length));
		}
		setTempPassword(password);
		setShowPassword(true); // Show the generated password
	};

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			handleClose();
		}
	};
	const tempPasswordId = useId();

	const copyTempPasswordToClipboard = () => {
		if (tempPassword) {
			navigator.clipboard.writeText(tempPassword);
			toast.success(t('admin.users.password.copied'));
		}
		if (!tempPassword) {
			toast.error(t('admin.users.password.noPasswordToCopy'));
		}
	};

	return (
		<Dialog onOpenChange={handleOpenChange} open={isOpen}>
			<DialogContent className="w-full max-w-md! px-0 pt-0 gap-0">
				{/* Header */}
				<DialogHeader className="px-6 py-4">
					<DialogTitle>{t('admin.users.password.title')}</DialogTitle>
				</DialogHeader>
				<Separator />
				<div>
					<div className="px-6 py-3 bg-card text-card-foreground">
						<p className="text-sm font-medium">
							{user?.firstName} {user?.lastName}
						</p>
						<p className="text-xs text-muted-foreground">{user?.email}</p>
					</div>
					{/* Tabs */}
					<Tabs defaultValue="temporary" className="flex flex-col bg-card text-card-foreground gap-0">
						<TabsList className="p-0 justify-start rounded-none border-b border-transparent bg-transparent w-full">
							<TabsTrigger
								value="temporary"
								className="h-auto rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-primary! data-[state=active]:bg-blue-50 data-[state=active]:text-primary! text-muted-foreground hover:text-primary! hover:cursor-pointer px-4 py-2 bg-transparent"
							>
								{t('admin.users.password.tabs.temporary')}
							</TabsTrigger>
							<TabsTrigger
								value="reset"
								className="h-auto rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-primary! data-[state=active]:bg-blue-50 data-[state=active]:text-primary! text-muted-foreground hover:text-primary! hover:cursor-pointer px-4 py-2 bg-transparent"
							>
								{t('admin.users.password.tabs.reset')}
							</TabsTrigger>
						</TabsList>
						<TabsContent value="temporary" className="flex-1 overflow-auto m-0 p-6 pb-0 bg-background text-foreground">
							<div className="space-y-4">
								<p className="text-sm text-muted-foreground">{t('admin.users.password.temporaryDescription')}</p>

								<div>
									<div className="flex items-center justify-between mb-2">
										<label htmlFor={tempPasswordId} className="flex flex-row justify-between w-full">
											<div className="text-sm font-medium">
												{t('admin.users.password.temporaryPassword')} <span className="text-red-600">*</span>
											</div>
											<button
												type="button"
												onClick={generateRandomPassword}
												className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 transition-colors"
												title={t('admin.users.password.generatePasswordTitle')}
											>
												<DicesIcon className="h-3.5 w-3.5" />
												<span>{t('admin.users.password.generate')}</span>
											</button>
										</label>
									</div>
									<div className="relative flex content-between">
										<Input
											id={tempPasswordId}
											type={showPassword ? 'text' : 'password'}
											placeholder={t('admin.users.password.temporaryPasswordPlaceholder')}
											autoComplete="off"
											className="bg-input"
											value={tempPassword}
											onChange={(e) => setTempPassword(e.target.value)}
										/>
										<div className="absolute top-0 right-0 flex">
											{tempPassword && (
												<Button
													className="hover:bg-transparent! text-muted-foreground hover:text-accent-foreground"
													onClick={copyTempPasswordToClipboard}
													size="icon"
													type="button"
													variant="ghost"
													title={t('admin.users.password.copyPasswordTitle')}
												>
													<CopyIcon className="size-4" />
												</Button>
											)}
											<Button
												className="hover:bg-transparent! text-muted-foreground hover:text-accent-foreground"
												onClick={() => setShowPassword(!showPassword)}
												size="icon"
												type="button"
												variant="ghost"
											>
												{showPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
											</Button>
										</div>
									</div>
									{tempPassword && tempPassword.length < 8 && (
										<p className="text-xs text-red-600 mt-1">{t('admin.users.validation.passwordMinLength')}</p>
									)}
								</div>

								<div className="flex items-center justify-end gap-3 pt-2">
									<Button type="button" variant="outline" onClick={handleClose}>
										{t('common.actions.cancel')}
									</Button>
									<Button
										type="button"
										onClick={handleSetTemporaryPassword}
										disabled={tempPassword.length < 8 || setTempPasswordApi.isPending}
									>
										{setTempPasswordApi.isPending
											? t('admin.users.password.setting')
											: t('admin.users.password.setPassword')}
									</Button>
								</div>
							</div>
						</TabsContent>

						<TabsContent value="reset" className="flex-1 overflow-auto m-0 p-6 pb-0 bg-background text-foreground">
							<div className="space-y-4">
								<p className="text-sm text-muted-foreground">
									{t('admin.users.password.resetDescription', { email: user?.email })}
								</p>

								<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
									<p className="text-sm text-blue-900">
										<strong>{t('admin.users.password.noteLabel')}</strong> {t('admin.users.password.resetNote')}
									</p>
								</div>

								<div className="flex items-center justify-end gap-3 pt-2">
									<Button type="button" variant="outline" onClick={handleClose}>
										{t('common.actions.cancel')}
									</Button>
									<Button type="button" onClick={handleSendResetEmail} disabled={sendPasswordResetEmailApi.isPending}>
										{sendPasswordResetEmailApi.isPending
											? t('admin.users.password.sending')
											: t('admin.users.password.sendReset')}
									</Button>
								</div>
							</div>
						</TabsContent>
					</Tabs>
				</div>
			</DialogContent>
		</Dialog>
	);
}
